import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useBusinessContext } from '../../../context/BusinessContext';
import { usePermissions } from '../../../context/PermissionsContext';
import { useTimeFormat, formatClock, TimeFormat } from '../../../util/timeFormat';
import { useAlert } from '../../../context/alert/AlertContext';
import { useNavigation } from '../../../context/NavigationContext';
import { useUser } from '../../../auth/UserContext';
import { Conversation, ChatMessage, TeamConversation, TeamMessage } from '../../../model/chat/Chat';
import { Client } from '../../../model/client/Client';
import { TeamMember } from '../../../model/teammember/TeamMember';
import {
    getBusinessConversations,
    getConversationMessages,
    sendBusinessMessage,
    markConversationReadByBusiness,
    uploadChatImage,
    startConversationWithClient,
    myTeamConversations,
    teamConversationMessages,
    startTeamConversation,
    sendTeamMessage,
    markTeamConversationRead,
    teamUnreadCount,
} from '../../../service/chat/chat-service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import { mediaUrl } from '../../../service/media/media-service';
import ChatImage from './ChatImage';
import { MENU } from '../../util/Utils';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import LoadingComponent from '../../commons/LoadingComponent';
import './MessagesComponent.css';

/** Derive the SSE base URL from the GraphQL URL by stripping the /graphql suffix. */
const CHAT_BASE = (process.env.REACT_APP_CHAT_SERVICE_BASE_URL ?? '').replace(/\/graphql\/?$/, '');

/* ---------- helpers ---------- */

/** Generate two uppercase initials from a name or fall back to '?' */
function initials(name?: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Format an ISO timestamp relative to now: today = "HH:mm", older = "dd/mm". */
function relativeTime(iso: string | null | undefined, fmt: TimeFormat = 'H24'): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const sameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    if (sameDay) {
        return formatClock(d, fmt);
    }
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}

/** Absolute date+time for the bubble timestamp tooltip. */
function absoluteTime(iso: string | null | undefined, fmt: TimeFormat = 'H24'): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString([], {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: fmt === 'AMPM',
    });
}

/** Full name from a Client record. */
function clientFullName(c: Client): string {
    return [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || String(c.id);
}

/* ---------- Avatar ---------- */

interface AvatarProps {
    src?: string | null;
    name?: string | null;
    size?: number;
    className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ src, name, size = 40, className }) => {
    const url = src ? mediaUrl(src) : '';
    return (
        <div
            className={`conv-avatar${className ? ` ${className}` : ''}`}
            style={{ width: size, height: size, minWidth: size, fontSize: size * 0.38 }}
        >
            {url ? (
                <img src={url} alt={name ?? 'avatar'} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
                initials(name)
            )}
        </div>
    );
};

/** Accepted image MIME types for chat attachments */
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/* ---------- MessagesComponent ---------- */

const MessagesComponent: React.FC = () => {
    const { t } = useTranslation();
    const timeFmt = useTimeFormat();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { pendingChatClient, setPendingChatClient, setPendingClient, setActiveMenu } = useNavigation();
    const { user } = useUser();

    const businessId = Number(selectedBusiness?.id) || 0;

    /* ---- state ---- */
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [convsLoading, setConvsLoading] = useState(true);
    const [search, setSearch] = useState('');

    /** Active tab: 'clients' | 'team' */
    // Client↔business conversations need CLIENT_CHAT (the backend gates every
    // client-chat op on it). The screen menu is gated on CHAT, so a member with
    // CHAT but not CLIENT_CHAT would otherwise see a Clientes tab whose actions the
    // backend rejects — hide it and keep them on Equipe.
    const { canView } = usePermissions();
    const canClientChat = canView('CLIENT_CHAT');
    const [activeTab, setActiveTab] = useState<'clients' | 'team'>('clients');
    useEffect(() => { if (!canClientChat) setActiveTab('team'); }, [canClientChat]);

    const [activeConv, setActiveConv] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [msgsLoading, setMsgsLoading] = useState(false);

    /** All clients for this business — used for name resolution + client picker. */
    const [clients, setClients] = useState<Client[]>([]);
    /** email (lowercased) → Client lookup map */
    const [clientByEmail, setClientByEmail] = useState<Map<string, Client>>(new Map());

    // Nova conversa picker
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [clientPickerSearch, setClientPickerSearch] = useState('');
    const [startingConv, setStartingConv] = useState(false);
    const clientPickerRef = useRef<HTMLDivElement | null>(null);

    // Composer (client thread)
    const [text, setText] = useState('');
    const [pendingKeys, setPendingKeys] = useState<string[]>([]);
    const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);

    // Emoji picker (client thread)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);
    const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // SSE subscription abort controller
    const sseAbortRef = useRef<AbortController | null>(null);
    // 60 s backstop interval ref
    const backstopIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // 12 s message backstop interval ref (open-thread safety net)
    const msgBackstopRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Auto-scroll and file input refs
    const msgsEndRef = useRef<HTMLDivElement | null>(null);
    // False until the first scroll-to-bottom for the open conversation has run,
    // so opening a thread JUMPS to the latest message instead of smooth-scrolling
    // through the whole history; later messages still animate. Reset on conv switch.
    const didInitialScrollRef = useRef(false);
    const didInitialTeamScrollRef = useRef(false);
    const newMsgsDividerRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Guard against stacking fetch calls while one is in flight
    const convsFetchingRef = useRef(false);
    const msgsFetchingRef = useRef(false);

    /* ---- Team-DM state (separate from client-chat state) ---- */
    const [teamConversations, setTeamConversations] = useState<TeamConversation[]>([]);
    const [teamConvsLoading, setTeamConvsLoading] = useState(false);
    const [activeTeamConv, setActiveTeamConv] = useState<TeamConversation | null>(null);
    const [teamMessages, setTeamMessages] = useState<TeamMessage[]>([]);
    const [teamMsgsLoading, setTeamMsgsLoading] = useState(false);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [teamMembersLoading, setTeamMembersLoading] = useState(false);
    const [teamText, setTeamText] = useState('');
    const [teamSending, setTeamSending] = useState(false);
    const [teamStarting, setTeamStarting] = useState(false);

    // Emoji picker (team thread)
    const [showTeamEmojiPicker, setShowTeamEmojiPicker] = useState(false);
    const teamEmojiPickerRef = useRef<HTMLDivElement | null>(null);
    const teamEmojiButtonRef = useRef<HTMLButtonElement | null>(null);
    const teamTextareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Auto-scroll ref for team thread
    const teamMsgsEndRef = useRef<HTMLDivElement | null>(null);

    /** Independent team unread count — polled on mount regardless of active tab,
     *  so the Equipe badge shows even while the Clientes tab is open. */
    const [teamUnreadTotal, setTeamUnreadTotal] = useState<number>(0);
    const teamUnreadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Guard against stacking fetch calls for team
    const teamConvsFetchingRef = useRef(false);
    const teamMsgsFetchingRef = useRef(false);

    // Polling refs for team tab
    const teamConvsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const teamMsgIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    /* ---- Independent team unread count poll — fires on mount, every 30 s ---- */
    useEffect(() => {
        if (!businessId) { setTeamUnreadTotal(0); return; }
        let cancelled = false;
        const fetch = async () => {
            try {
                const token = await getAccessTokenSilently();
                const count = await teamUnreadCount(businessId, token);
                if (!cancelled) setTeamUnreadTotal(count);
            } catch {
                // non-critical
            }
        };
        fetch();
        teamUnreadIntervalRef.current = setInterval(fetch, 30_000);
        return () => {
            cancelled = true;
            if (teamUnreadIntervalRef.current) { clearInterval(teamUnreadIntervalRef.current); teamUnreadIntervalRef.current = null; }
        };
    }, [businessId, getAccessTokenSilently]);

    /**
     * Pull the AUTHORITATIVE team unread total after a read/send and tell the nav
     * badge (LeftPanelComponent, a separate component) to re-poll immediately.
     * Without this the badges sit "stuck" on their last value until the next
     * 15-30 s poll — even though the server already dropped the count to 0.
     */
    const refreshUnreadBadges = useCallback(async () => {
        try {
            if (businessId) {
                const token = await getAccessTokenSilently();
                const count = await teamUnreadCount(businessId, token);
                setTeamUnreadTotal(count);
            }
        } catch {
            // non-critical — the periodic poll will reconcile
        }
        // Cross-component nudge so the left-nav "Mensagens" badge re-fetches now.
        window.dispatchEvent(new Event('gs-chat-unread-refresh'));
    }, [businessId, getAccessTokenSilently]);

    /**
     * How many messages were unread (unreadForBusiness) when the conversation was
     * opened. Used to render the "Novas mensagens" divider.  Captured BEFORE
     * mark-read zeroes the counter, so it stays stable for the life of the session.
     */
    const [unreadAtOpen, setUnreadAtOpen] = useState(0);

    /* ---- load clients once for name resolution ---- */
    useEffect(() => {
        if (!businessId) { setClients([]); setClientByEmail(new Map()); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const data = await getAllClientsByBusinessId(String(businessId), token);
                if (!cancelled) {
                    setClients(data);
                    const map = new Map<string, Client>();
                    data.forEach((c) => {
                        if (c.email) map.set(c.email.toLowerCase(), c);
                    });
                    setClientByEmail(map);
                }
            } catch {
                // Non-critical — name resolution degrades gracefully
            }
        })();
        return () => { cancelled = true; };
    }, [businessId, getAccessTokenSilently]);

    /* ---- Team: load team members once (for the contacts list) ---- */
    useEffect(() => {
        if (!businessId || activeTab !== 'team') return;
        let cancelled = false;
        setTeamMembersLoading(true);
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const members = await getTeamMembersByBusinessId(String(businessId), token);
                if (!cancelled) setTeamMembers(members);
            } catch {
                // Non-critical
            } finally {
                if (!cancelled) setTeamMembersLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [businessId, activeTab, getAccessTokenSilently]);

    /* ---- Team: fetch team conversations ---- */
    const fetchTeamConversations = useCallback(async () => {
        if (!businessId || teamConvsFetchingRef.current) return;
        teamConvsFetchingRef.current = true;
        try {
            const token = await getAccessTokenSilently();
            const list = await myTeamConversations(businessId, token);
            setTeamConversations(list);
        } catch {
            // Suppress polling errors silently
        } finally {
            teamConvsFetchingRef.current = false;
        }
    }, [businessId, getAccessTokenSilently]);

    // Load team conversations when team tab becomes active
    useEffect(() => {
        if (!businessId || activeTab !== 'team') return;
        setTeamConvsLoading(true);
        fetchTeamConversations().finally(() => setTeamConvsLoading(false));
    }, [businessId, activeTab, fetchTeamConversations]);

    /* ---- Team: 60 s polling for team conversations while tab is active ---- */
    useEffect(() => {
        if (!businessId || activeTab !== 'team') {
            if (teamConvsIntervalRef.current) { clearInterval(teamConvsIntervalRef.current); teamConvsIntervalRef.current = null; }
            return;
        }
        if (teamConvsIntervalRef.current) clearInterval(teamConvsIntervalRef.current);
        teamConvsIntervalRef.current = setInterval(() => {
            fetchTeamConversations();
        }, 60_000);
        return () => {
            if (teamConvsIntervalRef.current) { clearInterval(teamConvsIntervalRef.current); teamConvsIntervalRef.current = null; }
        };
    }, [businessId, activeTab, fetchTeamConversations]);

    /* ---- Team: fetch messages for active team conversation ---- */
    const fetchTeamMessages = useCallback(async (convId: string) => {
        if (teamMsgsFetchingRef.current) return;
        teamMsgsFetchingRef.current = true;
        try {
            const token = await getAccessTokenSilently();
            const list = await teamConversationMessages(convId, token);
            setTeamMessages(list);
        } catch {
            // Suppress polling errors
        } finally {
            teamMsgsFetchingRef.current = false;
        }
    }, [getAccessTokenSilently]);

    // When active team conversation changes: load messages and mark read
    useEffect(() => {
        if (!activeTeamConv?.id) { setTeamMessages([]); return; }
        const convId = activeTeamConv.id;
        setTeamMsgsLoading(true);
        fetchTeamMessages(convId).finally(() => setTeamMsgsLoading(false));

        // Mark read when opening
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                await markTeamConversationRead(convId, token);
                // Optimistically clear this row for snappiness…
                setTeamConversations((prev) =>
                    prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c))
                );
                // …then pull the authoritative totals so the badges can't stick.
                await refreshUnreadBadges();
            } catch {
                // non-critical
            }
        })();
    }, [activeTeamConv?.id, fetchTeamMessages, getAccessTokenSilently, refreshUnreadBadges]);

    /* ---- Team: 12 s message poll while a team conversation is open ---- */
    useEffect(() => {
        if (!activeTeamConv?.id || activeTab !== 'team') {
            if (teamMsgIntervalRef.current) { clearInterval(teamMsgIntervalRef.current); teamMsgIntervalRef.current = null; }
            return;
        }
        const convId = activeTeamConv.id;
        if (teamMsgIntervalRef.current) clearInterval(teamMsgIntervalRef.current);
        teamMsgIntervalRef.current = setInterval(() => {
            fetchTeamMessages(convId);
        }, 12_000);
        return () => {
            if (teamMsgIntervalRef.current) { clearInterval(teamMsgIntervalRef.current); teamMsgIntervalRef.current = null; }
        };
    }, [activeTeamConv?.id, activeTab, fetchTeamMessages]);

    // Reset the team thread's "first scroll" flag when switching conversations.
    useEffect(() => {
        didInitialTeamScrollRef.current = false;
    }, [activeTeamConv?.id]);

    // Auto-scroll to bottom of team thread: instant on open, smooth afterwards.
    useEffect(() => {
        const behavior: ScrollBehavior = didInitialTeamScrollRef.current ? 'smooth' : 'auto';
        teamMsgsEndRef.current?.scrollIntoView({ behavior });
        didInitialTeamScrollRef.current = true;
    }, [teamMessages]);

    /* ---- Team: emoji picker close on outside-click or Escape ---- */
    useEffect(() => {
        if (!showTeamEmojiPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                teamEmojiPickerRef.current && !teamEmojiPickerRef.current.contains(target) &&
                teamEmojiButtonRef.current && !teamEmojiButtonRef.current.contains(target)
            ) {
                setShowTeamEmojiPicker(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowTeamEmojiPicker(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showTeamEmojiPicker]);

    /** Insert emoji at caret position in the team composer. */
    const handleTeamEmojiClick = (emojiData: EmojiClickData) => {
        const emoji = emojiData.emoji;
        const el = teamTextareaRef.current;
        if (el) {
            const start = el.selectionStart ?? teamText.length;
            const end = el.selectionEnd ?? teamText.length;
            const next = teamText.slice(0, start) + emoji + teamText.slice(end);
            setTeamText(next);
            requestAnimationFrame(() => {
                el.focus();
                const pos = start + emoji.length;
                el.setSelectionRange(pos, pos);
            });
        } else {
            setTeamText((prev) => prev + emoji);
        }
    };

    /* ---- Team: start a DM from the contacts section ---- */
    const handleStartTeamDm = async (member: TeamMember) => {
        if (!member.email || !businessId) return;
        setTeamStarting(true);
        try {
            const token = await getAccessTokenSilently();
            const conv = await startTeamConversation(businessId, member.email, token);
            await fetchTeamConversations();
            setActiveTeamConv(conv);
            setTeamText('');
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('messages.teamChat.alerts.failStartConversation'));
        } finally {
            setTeamStarting(false);
        }
    };

    /* ---- Team: select an existing conversation ---- */
    const handleSelectTeamConv = async (conv: TeamConversation) => {
        setActiveTeamConv(conv);
        setTeamText('');
        if (conv.unread > 0) {
            // Optimistically clear this row…
            setTeamConversations((prev) =>
                prev.map((c) => (c.id === conv.id ? { ...c, unread: 0 } : c))
            );
            try {
                const token = await getAccessTokenSilently();
                await markTeamConversationRead(conv.id, token);
                // …then reconcile both badges against the server.
                await refreshUnreadBadges();
            } catch {
                // non-critical
            }
        }
    };

    /* ---- Team: send message ---- */
    const handleTeamSend = async () => {
        if (!activeTeamConv?.id) return;
        const trimmed = teamText.trim();
        if (!trimmed) return;
        setTeamSending(true);
        try {
            const token = await getAccessTokenSilently();
            const msg = await sendTeamMessage(activeTeamConv.id, trimmed, [], token);
            setTeamMessages((prev) => [...prev, msg]);
            setTeamText('');
            setTeamConversations((prev) =>
                prev.map((c) =>
                    c.id === activeTeamConv.id
                        ? { ...c, lastMessage: trimmed, lastAt: msg.createdAt }
                        : c
                )
            );
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('messages.teamChat.alerts.failSendMessage'));
        } finally {
            setTeamSending(false);
        }
    };

    const handleTeamKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleTeamSend();
        }
    };

    /* ---- Team: derive current user email (for self-exclusion in the contacts list) ---- */
    const currentUserEmail = (user?.email ?? '').toLowerCase();

    /* ---- team: resolve a teammate's display name from the loaded team list
            (the backend stores otherName=email as a fallback). ---- */
    const teamNameByEmail = useCallback(
        (email?: string | null): string | null => {
            if (!email) return null;
            const m = teamMembers.find((tm) => (tm.email ?? '').toLowerCase() === email.toLowerCase());
            const full = m ? [m.firstName, m.lastName].filter(Boolean).join(' ') : '';
            return full || null;
        },
        [teamMembers],
    );

    /** Resolve a teammate's phone from the loaded team list. */
    const teamPhoneByEmail = useCallback(
        (email?: string | null): string | null => {
            if (!email) return null;
            const m = teamMembers.find((tm) => (tm.email ?? '').toLowerCase() === email.toLowerCase());
            return m?.phone ?? null;
        },
        [teamMembers],
    );

    // Teammates already in a DM belong in CONVERSAS — don't repeat them in the
    // "Equipe" contacts list (start-new). Filter the contacts by: not me + no
    // existing conversation.
    const teamConvEmails = useMemo(
        () => new Set(teamConversations.map((c) => (c.otherEmail ?? '').toLowerCase())),
        [teamConversations],
    );
    const selectableTeamMembers = useMemo(
        () =>
            teamMembers.filter((m) => {
                const email = (m.email ?? '').toLowerCase();
                return email !== currentUserEmail && !teamConvEmails.has(email);
            }),
        [teamMembers, currentUserEmail, teamConvEmails],
    );

    /* ---- helper: display name for a conversation ---- */
    const convDisplayName = useCallback(
        (conv: Conversation): string => {
            const email = conv.clientEmail?.toLowerCase();
            if (email) {
                const c = clientByEmail.get(email);
                if (c) return clientFullName(c);
            }
            // clientName is sometimes just the email itself — prefer it only when it differs
            if (conv.clientName && conv.clientName !== conv.clientEmail) return conv.clientName;
            return conv.clientEmail ?? conv.clientId ?? '—';
        },
        [clientByEmail]
    );

    /* ---- helper: avatar for a conversation ----
       Prefer the client RECORD's avatar (refreshed on every booking AND message)
       over the conversation snapshot (only refreshed when the client messages) —
       so a photo changed before a booking shows here without a new message. */
    const convAvatar = useCallback(
        (conv: Conversation): string | null | undefined => {
            const email = conv.clientEmail?.toLowerCase();
            if (email) {
                const c = clientByEmail.get(email);
                if (c?.avatar) return c.avatar;
            }
            return conv.clientAvatar;
        },
        [clientByEmail]
    );

    /* ---- emoji picker: close on outside-click or Escape ---- */
    useEffect(() => {
        if (!showEmojiPicker) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                emojiPickerRef.current && !emojiPickerRef.current.contains(target) &&
                emojiButtonRef.current && !emojiButtonRef.current.contains(target)
            ) {
                setShowEmojiPicker(false);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowEmojiPicker(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showEmojiPicker]);

    /* ---- client picker: close on outside-click or Escape ---- */
    useEffect(() => {
        if (!showClientPicker) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (clientPickerRef.current && !clientPickerRef.current.contains(e.target as Node)) {
                setShowClientPicker(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowClientPicker(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showClientPicker]);

    /** Insert the selected emoji at the textarea caret position (or append). */
    const handleEmojiClick = (emojiData: EmojiClickData) => {
        const emoji = emojiData.emoji;
        const el = textareaRef.current;
        if (el) {
            const start = el.selectionStart ?? text.length;
            const end = el.selectionEnd ?? text.length;
            const next = text.slice(0, start) + emoji + text.slice(end);
            setText(next);
            // Restore focus and caret position after React re-render
            requestAnimationFrame(() => {
                el.focus();
                const pos = start + emoji.length;
                el.setSelectionRange(pos, pos);
            });
        } else {
            setText((prev) => prev + emoji);
        }
    };

    /* ---- fetch conversations ---- */
    const fetchConversations = useCallback(async () => {
        if (!businessId || convsFetchingRef.current) return;
        convsFetchingRef.current = true;
        try {
            const token = await getAccessTokenSilently();
            const list = await getBusinessConversations(businessId, token);
            setConversations(list);
        } catch (e) {
            // Suppress polling errors silently — only show on first load
        } finally {
            convsFetchingRef.current = false;
        }
    }, [businessId, getAccessTokenSilently]);

    // Initial load (show spinner)
    useEffect(() => {
        if (!businessId) { setConvsLoading(false); return; }
        setConvsLoading(true);
        fetchConversations().finally(() => setConvsLoading(false));
    }, [businessId, fetchConversations]);

    /* ---- fetch messages for active conversation ---- */
    const fetchMessages = useCallback(async (convId: string) => {
        if (msgsFetchingRef.current) return;
        msgsFetchingRef.current = true;
        try {
            const token = await getAccessTokenSilently();
            const list = await getConversationMessages(convId, token);
            setMessages(list);
        } catch (e) {
            // Suppress errors — SSE will trigger retries
        } finally {
            msgsFetchingRef.current = false;
        }
    }, [getAccessTokenSilently]);

    // When active conversation changes: clear messages, load fresh (SSE drives future updates)
    useEffect(() => {
        if (!activeConv?.id) { setMessages([]); return; }

        const convId = activeConv.id;
        setMsgsLoading(true);
        fetchMessages(convId).finally(() => setMsgsLoading(false));
    }, [activeConv?.id, fetchMessages]);

    /* ---- Stable ref: tracks the active conversation for use inside the SSE closure
           without forcing the SSE subscription to re-open on every conversation change. ---- */
    const activeConvForSSERef = useRef<Conversation | null>(activeConv);
    useEffect(() => {
        activeConvForSSERef.current = activeConv;
    }); // runs after every render — keeps ref in sync cheaply

    /* ---- SSE subscription — one stream per businessId ---- */
    useEffect(() => {
        if (!businessId || !CHAT_BASE) return;

        // Abort any previous subscription
        if (sseAbortRef.current) sseAbortRef.current.abort();
        const controller = new AbortController();
        sseAbortRef.current = controller;

        const startSSE = async () => {
            let token: string;
            try {
                token = await getAccessTokenSilently();
            } catch {
                // Can't start without a token — 60 s backstop will cover us
                return;
            }

            fetchEventSource(`${CHAT_BASE}/sse/business/${businessId}`, {
                signal: controller.signal,
                headers: { Authorization: `Bearer ${token}` },
                onopen: async (response) => {
                    if (response.ok) return; // stream opened successfully
                    // Non-retriable status (401, 403…) — throw so onerror fires
                    throw new Error(`SSE open failed: ${response.status}`);
                },
                onmessage: (ev) => {
                    if (ev.event !== 'change') return; // skip pings and empty lines

                    // Always refresh conversations (updates previews + unread counts)
                    fetchConversations();

                    // Any change event for this business while a conversation is open
                    // → also refresh the open thread so new messages appear immediately.
                    const openConvId = activeConvForSSERef.current?.id;
                    if (openConvId) {
                        fetchMessages(openConvId);
                    }
                },
                onerror: (err) => {
                    if (controller.signal.aborted) return; // intentional teardown — stop
                    console.warn('[SSE] connection error, will reconnect:', err);
                    // Returning void lets fetch-event-source auto-reconnect after its
                    // default back-off. A fresh token will be obtained on the next onopen.
                },
            }).catch((err) => {
                if (!controller.signal.aborted) {
                    console.warn('[SSE] stream ended unexpectedly:', err);
                }
            });
        };

        startSSE();

        return () => {
            controller.abort();
            sseAbortRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]); // intentionally omits activeConvForSSERef — it's a mutable ref, not state

    /* ---- 60 s backstop: refetch conversations in case an SSE event was missed ---- */
    useEffect(() => {
        if (!businessId) return;
        backstopIntervalRef.current = setInterval(() => {
            fetchConversations();
            // Also refresh open thread so it doesn't drift
            const openConvId = activeConvForSSERef.current?.id;
            if (openConvId) fetchMessages(openConvId);
        }, 60_000);
        return () => {
            if (backstopIntervalRef.current) clearInterval(backstopIntervalRef.current);
        };
    }, [businessId, fetchConversations, fetchMessages]);

    /* ---- 12 s message backstop: safety-net poll while a conversation is open ---- */
    useEffect(() => {
        if (!activeConv?.id) {
            if (msgBackstopRef.current) { clearInterval(msgBackstopRef.current); msgBackstopRef.current = null; }
            return;
        }
        const convId = activeConv.id;
        if (msgBackstopRef.current) clearInterval(msgBackstopRef.current);
        msgBackstopRef.current = setInterval(() => {
            fetchMessages(convId);
        }, 12_000);
        return () => {
            if (msgBackstopRef.current) { clearInterval(msgBackstopRef.current); msgBackstopRef.current = null; }
        };
    }, [activeConv?.id, fetchMessages]);

    // Reset the "first scroll" flag whenever a different conversation opens, so
    // it jumps straight to the latest message on open.
    useEffect(() => {
        didInitialScrollRef.current = false;
    }, [activeConv?.id]);

    // Auto-scroll to bottom: instant on open (no scrolling through history),
    // smooth for messages that arrive while you're viewing the thread.
    useEffect(() => {
        const behavior: ScrollBehavior = didInitialScrollRef.current ? 'smooth' : 'auto';
        msgsEndRef.current?.scrollIntoView({ behavior });
        didInitialScrollRef.current = true;
    }, [messages]);

    /* ---- Auto-mark-read: whenever messages refresh for the open conversation
           and the conversation list still shows unread > 0, call mark-read so
           the badge never sticks on the actively-viewed thread. ---- */
    useEffect(() => {
        if (!activeConv?.id) return;
        const convId = activeConv.id;
        const currentUnread = conversations.find((c) => c.id === convId)?.unreadForBusiness ?? 0;
        if (currentUnread <= 0) return;

        // Optimistically zero the badge immediately
        setConversations((prev) =>
            prev.map((c) => (c.id === convId ? { ...c, unreadForBusiness: 0 } : c))
        );
        // Fire-and-forget the server call
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                await markConversationReadByBusiness(convId, token);
                // Nudge the left-nav "Mensagens" badge (client + team) to re-poll now.
                window.dispatchEvent(new Event('gs-chat-unread-refresh'));
            } catch {
                // non-critical — badge will re-sync on next conversation refresh
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]); // trigger on every messages refresh while a conv is open

    /* ---- start conversation (shared logic) ---- */
    const doStartConversation = useCallback(async (
        clientEmail: string,
        clientName?: string | null
    ): Promise<Conversation | null> => {
        if (!businessId) return null;
        try {
            const token = await getAccessTokenSilently();
            const conv = await startConversationWithClient(
                {
                    businessId,
                    clientEmail,
                    clientName: clientName ?? null,
                    businessName: selectedBusiness?.name ?? null,
                    businessImage: selectedBusiness?.image ?? null,
                    businessSlug: null,
                },
                token
            );
            // Refresh conversation list to include the new/existing thread
            await fetchConversations();
            return conv;
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('messages.alerts.failStartConversation'));
            return null;
        }
    }, [businessId, getAccessTokenSilently, selectedBusiness, fetchConversations, setAlert]);

    /* ---- pendingChatClient: auto-start a chat when navigated from ClientsComponent ---- */
    useEffect(() => {
        if (!pendingChatClient || !businessId) return;
        const { email, name } = pendingChatClient;
        // Clear immediately to prevent re-trigger
        setPendingChatClient(null);

        setStartingConv(true);
        doStartConversation(email, name).then((conv) => {
            if (conv) {
                setUnreadAtOpen(0);
                setActiveConv(conv);
            }
        }).finally(() => setStartingConv(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingChatClient]);

    /* ---- select conversation ---- */
    const handleSelectConv = async (conv: Conversation) => {
        // Capture how many messages were unread at open time BEFORE zeroing.
        // This is the count used to position the "Novas mensagens" divider.
        const unread = conv.unreadForBusiness ?? 0;
        setUnreadAtOpen(unread);

        setActiveConv(conv);
        setText('');
        setPendingKeys([]);
        setPendingPreviews([]);

        // Mark as read optimistically
        if (unread > 0 && conv.id) {
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === conv.id ? { ...c, unreadForBusiness: 0 } : c
                )
            );
            try {
                const token = await getAccessTokenSilently();
                await markConversationReadByBusiness(conv.id, token);
            } catch {
                // non-critical
            }
        }
    };

    /* ---- start new conversation from client picker ---- */
    const handlePickClient = async (client: Client) => {
        if (!client.email) {
            setAlert('danger', t('messages.alerts.noEmailForChat'));
            return;
        }
        // Chat delivers to the consumer ACCOUNT inbox — an email without an
        // account has no "other side". Explicit false only: unknown (lookup
        // failed) still goes through and the backend gives the final word.
        if (client.registered === false) {
            setAlert('danger', t('messages.alerts.notRegisteredForChat'));
            return;
        }
        setShowClientPicker(false);
        setClientPickerSearch('');
        setStartingConv(true);
        try {
            const conv = await doStartConversation(client.email, clientFullName(client));
            if (conv) {
                setUnreadAtOpen(0);
                setActiveConv(conv);
            }
        } finally {
            setStartingConv(false);
        }
    };

    /* ---- "Ver cliente" button ---- */
    const handleViewClient = () => {
        if (!activeConv?.clientEmail) return;
        const c = clientByEmail.get(activeConv.clientEmail.toLowerCase());
        if (!c) return;
        setPendingClient(c.id, 'info');
        setActiveMenu(MENU.CLIENT);
    };

    /* ---- image attach ---- */
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const files = Array.from(e.target.files);
        // Reset the input so the same file can be selected again
        e.target.value = '';

        // Validate file types before uploading
        const invalid = files.filter((f) => !ACCEPTED_IMAGE_TYPES.includes(f.type));
        if (invalid.length > 0) {
            const names = invalid.map((f) => f.name).join(', ');
            setAlert('danger', t('messages.alerts.unsupportedFileType', { names }));
            return;
        }

        setUploading(true);
        try {
            const token = await getAccessTokenSilently();
            const uploaded = await Promise.all(
                files.map(async (file) => {
                    // Pass conversationId so the backend files the image under the correct path
                    const key = await uploadChatImage(file, token, activeConv?.id ?? null);
                    const preview = URL.createObjectURL(file);
                    return { key, preview };
                })
            );
            setPendingKeys((prev) => [...prev, ...uploaded.map((u) => u.key)]);
            setPendingPreviews((prev) => [...prev, ...uploaded.map((u) => u.preview)]);
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('messages.alerts.failUploadImage'));
        } finally {
            setUploading(false);
        }
    };

    const removePending = (idx: number) => {
        setPendingKeys((prev) => prev.filter((_, i) => i !== idx));
        setPendingPreviews((prev) => {
            URL.revokeObjectURL(prev[idx]);
            return prev.filter((_, i) => i !== idx);
        });
    };

    /* ---- send message ---- */
    const handleSend = async () => {
        if (!activeConv?.id) return;
        const trimmed = text.trim();
        if (!trimmed && pendingKeys.length === 0) return;

        setSending(true);
        try {
            const token = await getAccessTokenSilently();
            const msg = await sendBusinessMessage(
                activeConv.id,
                trimmed || null,
                pendingKeys,
                token
            );
            setMessages((prev) => [...prev, msg]);
            setText('');
            setPendingKeys([]);
            setPendingPreviews((prev) => { prev.forEach((u) => URL.revokeObjectURL(u)); return []; });

            // Optimistically update the conversation preview in the list
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === activeConv.id
                        ? {
                              ...c,
                              lastMessageText: trimmed || '[imagem]',
                              lastMessageAt: msg.createdAt ?? new Date().toISOString(),
                              lastSenderType: 'BUSINESS',
                          }
                        : c
                )
            );
        } catch (err) {
            setAlert('danger', err instanceof Error ? err.message : t('messages.alerts.failSendMessage'));
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    /* ---- per-tab unread totals for tab badges ---- */
    const clientsUnreadTotal = conversations.reduce((sum, c) => sum + (c.unreadForBusiness ?? 0), 0);

    /* ---- filter conversations ---- */
    const q = search.trim().toLowerCase();
    const filteredConvs = q
        ? conversations.filter((c) => {
              const name = convDisplayName(c).toLowerCase();
              return name.includes(q) || (c.clientEmail ?? '').toLowerCase().includes(q);
          })
        : conversations;

    /* ---- client picker filter ---- */
    const pickerQ = clientPickerSearch.trim().toLowerCase();
    const filteredClients = pickerQ
        ? clients.filter((c) => {
              const haystack = [c.firstName, c.lastName, c.email, c.phone]
                  .filter(Boolean).join(' ').toLowerCase();
              return haystack.includes(pickerQ);
          })
        : clients;

    /* ---- "Ver cliente" state ---- */
    const activeConvClient = activeConv?.clientEmail
        ? clientByEmail.get(activeConv.clientEmail.toLowerCase()) ?? null
        : null;

    /* ---- user display data ---- */
    const userDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
    const userAvatarSrc = user?.image || null;

    /* ---- no business ---- */
    if (!businessId) {
        return (
            <div className="messages-page" style={{ display: 'block', padding: '2rem', textAlign: 'center', color: 'var(--gs-text-soft)' }}>
                {t('messages.selectBusiness')}
            </div>
        );
    }

    /* ---- render ---- */
    return (
        <div className="messages-page" data-cy="messages-page">
            {/* ======== LEFT: conversation list ======== */}
            <div className="msg-card conv-list-card">

                {/* Top bar: user avatar + search + new conversation button */}
                <div className="conv-top-bar">
                    {/* Current user avatar with green online dot */}
                    <div className="conv-top-user" title={userDisplayName}>
                        <div className="conv-top-user-avatar">
                            {userAvatarSrc ? (
                                <img
                                    src={userAvatarSrc}
                                    alt={userDisplayName}
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                            ) : (
                                initials(userDisplayName)
                            )}
                        </div>
                        <span className="conv-top-user-dot" aria-hidden />
                    </div>

                    {/* Full-width rounded search input */}
                    <div className="conv-top-search">
                        <span className="conv-top-search__icon" aria-hidden>
                            {/* magnifier SVG */}
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" strokeWidth="1.8"/>
                                <path d="M13 13L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                        </span>
                        <input
                            type="text"
                            placeholder={t('messages.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-cy="messages-search-input"
                        />
                        {search && (
                            <button
                                type="button"
                                className="conv-top-search__clear"
                                onClick={() => setSearch('')}
                                aria-label={t('messages.clearSearch')}
                                title={t('common.clear')}
                            >
                                &times;
                            </button>
                        )}
                    </div>

                    {/* Nova conversa button */}
                    <div className="msg-nova-conv-wrap conv-top-newbtn" ref={clientPickerRef}>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                                setShowClientPicker((v) => !v);
                                setClientPickerSearch('');
                            }}
                            disabled={startingConv}
                            title={t('messages.newConversationTitle')}
                        >
                            {startingConv ? '...' : t('messages.newConversation')}
                        </button>

                        {showClientPicker && (
                            <div className="msg-client-picker">
                                <div className="gs-list-search" style={{ margin: '0 0 0.4rem' }}>
                                    <span className="gs-list-search__icon" aria-hidden>{'⌕'}</span>
                                    <input
                                        autoFocus
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder={t('messages.clientSearchPlaceholder')}
                                        value={clientPickerSearch}
                                        onChange={(e) => setClientPickerSearch(e.target.value)}
                                    />
                                    {clientPickerSearch && (
                                        <button
                                            type="button"
                                            className="gs-list-search__clear"
                                            onClick={() => setClientPickerSearch('')}
                                            aria-label={t('common.clear')}
                                            title={t('common.clear')}
                                        >
                                            &times;
                                        </button>
                                    )}
                                </div>
                                <div className="msg-client-picker__list">
                                    {filteredClients.length === 0 ? (
                                        <div className="msg-client-picker__empty">
                                            {clients.length === 0
                                                ? t('messages.noClientsRegistered')
                                                : t('messages.noClientsFound')}
                                        </div>
                                    ) : (
                                        filteredClients.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                className={`msg-client-picker__item${!c.email || c.registered === false ? ' msg-client-picker__item--noemail' : ''}`}
                                                onClick={() => handlePickClient(c)}
                                                title={!c.email ? t('messages.noEmailTitle')
                                                    : c.registered === false ? t('messages.notRegisteredTitle') : undefined}
                                            >
                                                <span className="msg-client-picker__avatar">
                                                    {initials(clientFullName(c))}
                                                </span>
                                                <span className="msg-client-picker__info">
                                                    <span className="msg-client-picker__name">{clientFullName(c)}</span>
                                                    {c.email ? (
                                                        <span className="msg-client-picker__email">
                                                            {c.email}
                                                            {c.registered === false && (
                                                                <span className="msg-client-picker__noemail"> · {t('messages.notRegistered')}</span>
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="msg-client-picker__noemail">{t('messages.noEmail')}</span>
                                                    )}
                                                </span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tab bar: Clientes | Equipe */}
                <div className="conv-tabs">
                    {canClientChat && (
                    <button
                        type="button"
                        className={`conv-tab${activeTab === 'clients' ? ' conv-tab--active' : ''}`}
                        onClick={() => setActiveTab('clients')}
                    >
                        {t('messages.tabs.clients')}
                        {clientsUnreadTotal > 0 && (
                            <span className="conv-tab__badge">
                                {clientsUnreadTotal > 9 ? '9+' : clientsUnreadTotal}
                            </span>
                        )}
                    </button>
                    )}
                    <button
                        type="button"
                        className={`conv-tab${activeTab === 'team' ? ' conv-tab--active' : ''}`}
                        onClick={() => setActiveTab('team')}
                    >
                        {t('messages.tabs.team')}
                        {teamUnreadTotal > 0 && (
                            <span className="conv-tab__badge">
                                {teamUnreadTotal > 9 ? '9+' : teamUnreadTotal}
                            </span>
                        )}
                    </button>
                </div>

                {/* Clients tab */}
                {activeTab === 'clients' && (
                    <>
                        <div className="conv-section-label">{t('messages.conversationsLabel')}</div>
                        <div className="conv-list-scroll">
                            {convsLoading ? (
                                <div style={{ padding: '2rem' }}>
                                    <LoadingComponent />
                                </div>
                            ) : filteredConvs.length === 0 ? (
                                <div className="msg-empty">
                                    <div className="msg-empty__icon-circle">
                                        <span role="img" aria-hidden>&#128172;</span>
                                    </div>
                                    <span className="msg-empty__text">
                                        {q ? t('messages.noConversationsSearch', { search }) : t('messages.noConversations')}
                                    </span>
                                </div>
                            ) : (
                                filteredConvs.map((conv) => {
                                    const displayName = convDisplayName(conv);
                                    const email = conv.clientEmail?.toLowerCase();
                                    const matchedClient = email ? clientByEmail.get(email) : undefined;
                                    const unread = (conv.unreadForBusiness ?? 0) > 0;
                                    return (
                                        <div
                                            key={conv.id ?? conv.clientId}
                                            className={`conv-item${activeConv?.id === conv.id ? ' active' : ''}${unread ? ' conv-item--unread' : ''}`}
                                            onClick={() => handleSelectConv(conv)}
                                            data-cy={`messages-conversation-row-${conv.id ?? conv.clientId}`}
                                        >
                                            {/* Avatar with status dot */}
                                            <div className="conv-avatar-wrap">
                                                <Avatar
                                                    src={convAvatar(conv)}
                                                    name={matchedClient ? clientFullName(matchedClient) : displayName}
                                                    size={44}
                                                />
                                                <span
                                                    className={`conv-status-dot${conv.lastSenderType === 'CLIENT' ? '' : ' conv-status-dot--offline'}`}
                                                    aria-hidden
                                                />
                                            </div>

                                            <div className="conv-info">
                                                <div className="conv-name">{displayName}</div>
                                                {conv.lastMessageText && (
                                                    <div className="conv-preview">{conv.lastMessageText}</div>
                                                )}
                                            </div>

                                            <div className="conv-meta">
                                                <span className="conv-time">{relativeTime(conv.lastMessageAt, timeFmt)}</span>
                                                {unread && (
                                                    <span className="conv-unread">{conv.unreadForBusiness}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}

                {/* Team tab */}
                {activeTab === 'team' && (
                    <div className="conv-list-scroll" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                        {/* Conversations section */}
                        <div className="conv-section-label">{t('messages.teamChat.conversationsLabel')}</div>
                        <div style={{ overflowY: 'auto', flex: '0 0 auto', maxHeight: '45%' }}>
                            {teamConvsLoading ? (
                                <div style={{ padding: '1rem' }}><LoadingComponent /></div>
                            ) : teamConversations.length === 0 ? (
                                <div className="msg-empty" style={{ padding: '1rem 1rem 0.5rem', flex: 'none', justifyContent: 'flex-start', gap: '0.4rem' }}>
                                    <span className="msg-empty__text" style={{ fontSize: '0.8rem' }}>{t('messages.teamChat.noConversations')}</span>
                                </div>
                            ) : (
                                teamConversations.map((conv) => {
                                    const displayName = teamNameByEmail(conv.otherEmail) || conv.otherName || conv.otherEmail;
                                    const hasUnread = conv.unread > 0;
                                    return (
                                        <div
                                            key={conv.id}
                                            className={`conv-item${activeTeamConv?.id === conv.id ? ' active' : ''}${hasUnread ? ' conv-item--unread' : ''}`}
                                            onClick={() => handleSelectTeamConv(conv)}
                                        >
                                            <div className="conv-avatar-wrap">
                                                <Avatar name={displayName} size={44} />
                                                <span className="conv-status-dot conv-status-dot--offline" aria-hidden />
                                            </div>
                                            <div className="conv-info">
                                                <div className="conv-name">{displayName}</div>
                                                {conv.lastMessage && (
                                                    <div className="conv-preview">{conv.lastMessage}</div>
                                                )}
                                            </div>
                                            <div className="conv-meta">
                                                <span className="conv-time">{relativeTime(conv.lastAt, timeFmt)}</span>
                                                {hasUnread && (
                                                    <span className="conv-unread">{conv.unread}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Team contacts section */}
                        <div className="conv-section-label" style={{ marginTop: '0.4rem' }}>{t('messages.teamChat.teamLabel')}</div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {teamMembersLoading ? (
                                <div style={{ padding: '1rem' }}><LoadingComponent /></div>
                            ) : selectableTeamMembers.length === 0 ? (
                                <div className="msg-empty" style={{ padding: '1rem 1rem 0.5rem', flex: 'none', justifyContent: 'flex-start', gap: '0.4rem' }}>
                                    <span className="msg-empty__text" style={{ fontSize: '0.8rem' }}>{t('messages.teamChat.noTeamMembers')}</span>
                                </div>
                            ) : (
                                selectableTeamMembers
                                    .map((member) => {
                                        const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ');
                                        const canMessage = !!member.email && !!member.hasAppAccess;
                                        return (
                                            <div
                                                key={member.id}
                                                className={`conv-item${!canMessage ? ' msg-client-picker__item--noemail' : ''}`}
                                                style={{ cursor: canMessage ? 'pointer' : 'not-allowed', opacity: canMessage ? 1 : 0.5 }}
                                                onClick={() => canMessage && !teamStarting ? handleStartTeamDm(member) : undefined}
                                                title={!member.email ? t('messages.teamChat.noAppAccess') : !member.hasAppAccess ? t('messages.teamChat.noAppAccess') : t('messages.teamChat.startDm')}
                                            >
                                                <div className="conv-avatar-wrap">
                                                    <Avatar name={fullName || member.email} size={44} />
                                                </div>
                                                <div className="conv-info">
                                                    <div className="conv-name">{fullName || member.email || '—'}</div>
                                                    {member.email && (
                                                        <div className="conv-preview">{member.email}</div>
                                                    )}
                                                </div>
                                                {teamStarting && activeTeamConv === null && (
                                                    <div className="conv-meta">
                                                        <span className="conv-time">…</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ======== RIGHT: thread pane ======== */}
            <div className="msg-card thread-card" data-cy="messages-thread">
                {/* ---- CLIENT thread ---- */}
                {activeTab === 'clients' && (
                    !activeConv ? (
                        /* Empty state — no conversation selected */
                        <div className="msg-empty" style={{ flex: 1 }}>
                            <div className="msg-empty__icon-circle">
                                <span role="img" aria-hidden>&#128172;</span>
                            </div>
                            <span className="msg-empty__text">{t('messages.selectContact')}</span>
                            <span className="msg-empty__sub">{t('messages.selectContactSub')}</span>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="thread-header">
                                <div className="thread-header-avatar-wrap">
                                    <Avatar
                                        src={convAvatar(activeConv)}
                                        name={activeConvClient ? clientFullName(activeConvClient) : convDisplayName(activeConv)}
                                        size={40}
                                    />
                                    <span className="thread-header-dot" aria-hidden />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="thread-client-name">
                                        {activeConvClient ? clientFullName(activeConvClient) : convDisplayName(activeConv)}
                                    </div>
                                    <div className="thread-contact-bar">
                                        {activeConv.clientEmail && (
                                            <a
                                                className="thread-contact-chip"
                                                href={`mailto:${activeConv.clientEmail}`}
                                                title={activeConv.clientEmail}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                                {activeConv.clientEmail}
                                            </a>
                                        )}
                                        {activeConvClient?.phone && (
                                            <a
                                                className="thread-contact-chip"
                                                href={`tel:${activeConvClient.phone.replace(/[^+\d]/g, '')}`}
                                                title={activeConvClient.phone}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                                {activeConvClient.phone}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {/* Ver cliente button — only when a matching registered client exists */}
                                {activeConvClient ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={handleViewClient}
                                        title={t('messages.viewClientTitle')}
                                    >
                                        {t('messages.viewClient')}
                                    </button>
                                ) : activeConv.clientEmail ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled
                                        title={t('messages.clientNotRegisteredTitle')}
                                    >
                                        {t('messages.viewClient')}
                                    </button>
                                ) : null}
                            </div>

                            {/* Messages */}
                            <div className="thread-messages">
                                {msgsLoading ? (
                                    <div style={{ margin: 'auto' }}>
                                        <LoadingComponent />
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="msg-empty">
                                        <div className="msg-empty__icon-circle">
                                            <span role="img" aria-hidden>&#128172;</span>
                                        </div>
                                        <span className="msg-empty__text">{t('messages.noMessages')}</span>
                                    </div>
                                ) : (() => {
                                    // Index of the first "new" (unread) message.
                                    // unreadAtOpen is how many messages were unread when the conversation
                                    // was opened.  The divider goes before messages[dividerIndex].
                                    // If unreadAtOpen is 0, no divider.
                                    // If unreadAtOpen >= total, the divider would be at index 0 —
                                    // suppress it to avoid a divider covering the whole thread.
                                    const dividerIndex =
                                        unreadAtOpen > 0 && unreadAtOpen < messages.length
                                            ? messages.length - unreadAtOpen
                                            : -1;

                                    return messages.map((msg, idx) => {
                                        const isBusiness = msg.senderType === 'BUSINESS';
                                        const clientAvatarName = activeConvClient
                                            ? clientFullName(activeConvClient)
                                            : convDisplayName(activeConv);
                                        return (
                                            <React.Fragment key={msg.id ?? idx}>
                                                {idx === dividerIndex && (
                                                    <div
                                                        className="new-msgs-divider"
                                                        ref={newMsgsDividerRef}
                                                    >
                                                        <span className="new-msgs-divider__line" />
                                                        <span className="new-msgs-divider__label">{t('messages.newMessages')}</span>
                                                        <span className="new-msgs-divider__line" />
                                                    </div>
                                                )}
                                                <div
                                                    className={`bubble-row ${isBusiness ? 'bubble-row--business' : 'bubble-row--client'}`}
                                                >
                                                    {!isBusiness && (
                                                        <Avatar
                                                            src={convAvatar(activeConv)}
                                                            name={clientAvatarName}
                                                            size={28}
                                                        />
                                                    )}
                                                    <div style={{ maxWidth: '72%' }}>
                                                        <div className={`bubble ${isBusiness ? 'bubble--business' : 'bubble--client'}`}>
                                                            {msg.text && <span>{msg.text}</span>}
                                                            {(msg.imageKeys ?? []).length > 0 && (
                                                                <div className="bubble-images">
                                                                    {(msg.imageKeys ?? []).map((key, ki) => (
                                                                        <ChatImage key={ki} imageKey={key} alt={t('messages.imageAlt')} className="bubble-img" />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="bubble-time" title={absoluteTime(msg.createdAt, timeFmt)}>
                                                            {absoluteTime(msg.createdAt, timeFmt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                                <div ref={msgsEndRef} />
                            </div>

                            {/* Composer */}
                            <div className="composer">
                                {/* Pending attachment previews */}
                                {pendingPreviews.length > 0 && (
                                    <div className="composer-attachments">
                                        {pendingPreviews.map((url, idx) => (
                                            <div key={idx} className="composer-attach-preview">
                                                <img src={url} alt="anexo" />
                                                <button
                                                    type="button"
                                                    className="composer-attach-remove"
                                                    onClick={() => removePending(idx)}
                                                    aria-label={t('messages.removeImage')}
                                                    title={t('common.remove')}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Emoji picker — rendered above the composer row */}
                                {showEmojiPicker && (
                                    <div className="composer-emoji-picker" ref={emojiPickerRef}>
                                        <EmojiPicker
                                            onEmojiClick={handleEmojiClick}
                                            theme={Theme.AUTO}
                                            lazyLoadEmojis
                                            searchPlaceholder={t('messages.emojiSearchPlaceholder')}
                                            width="100%"
                                            height={340}
                                        />
                                    </div>
                                )}

                                <div className="composer-row">
                                    {/* Emoji button */}
                                    <button
                                        ref={emojiButtonRef}
                                        type="button"
                                        className={`composer-btn composer-btn--emoji${showEmojiPicker ? ' active' : ''}`}
                                        onClick={() => setShowEmojiPicker((v) => !v)}
                                        disabled={sending}
                                        title={t('messages.emoji')}
                                        aria-label={t('messages.emojiPickerAriaLabel')}
                                        aria-expanded={showEmojiPicker}
                                    >
                                        <span role="img" aria-hidden>&#128522;</span>
                                    </button>

                                    {/* Attach button */}
                                    <button
                                        type="button"
                                        className="composer-btn composer-btn--attach"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading || sending}
                                        title={t('messages.attachImage')}
                                        aria-label={t('messages.attachImage')}
                                    >
                                        {uploading ? (
                                            <span style={{ fontSize: '0.7rem' }}>...</span>
                                        ) : (
                                            /* paperclip SVG */
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                            </svg>
                                        )}
                                    </button>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp,image/gif"
                                        multiple
                                        className="composer-file-input"
                                        onChange={handleFileChange}
                                    />

                                    <textarea
                                        ref={textareaRef}
                                        className="composer-textarea"
                                        rows={1}
                                        placeholder={t('messages.composerPlaceholder')}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        disabled={sending}
                                        data-cy="messages-input"
                                    />

                                    <button
                                        type="button"
                                        className="composer-btn composer-btn--send"
                                        onClick={handleSend}
                                        disabled={sending || uploading || (!text.trim() && pendingKeys.length === 0)}
                                        title={t('messages.send')}
                                        aria-label={t('messages.sendAriaLabel')}
                                        data-cy="messages-send-btn"
                                    >
                                        {/* send arrow SVG */}
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )
                )}

                {/* ---- TEAM thread ---- */}
                {activeTab === 'team' && (
                    !activeTeamConv ? (
                        /* Empty state — no team conversation selected */
                        <div className="msg-empty" style={{ flex: 1 }}>
                            <div className="msg-empty__icon-circle">
                                <span role="img" aria-hidden>&#128101;</span>
                            </div>
                            <span className="msg-empty__text">{t('messages.selectContact')}</span>
                            <span className="msg-empty__sub">{t('messages.selectContactSub')}</span>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="thread-header">
                                <div className="thread-header-avatar-wrap">
                                    <Avatar
                                        name={(teamNameByEmail(activeTeamConv.otherEmail) ?? activeTeamConv.otherName ?? activeTeamConv.otherEmail)}
                                        size={40}
                                    />
                                    <span className="thread-header-dot" aria-hidden />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="thread-client-name">
                                        {(teamNameByEmail(activeTeamConv.otherEmail) ?? activeTeamConv.otherName ?? activeTeamConv.otherEmail)}
                                    </div>
                                    <div className="thread-contact-bar">
                                        {activeTeamConv.otherEmail && (
                                            <a
                                                className="thread-contact-chip"
                                                href={`mailto:${activeTeamConv.otherEmail}`}
                                                title={activeTeamConv.otherEmail}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                                {activeTeamConv.otherEmail}
                                            </a>
                                        )}
                                        {teamPhoneByEmail(activeTeamConv.otherEmail) && (
                                            <a
                                                className="thread-contact-chip"
                                                href={`tel:${(teamPhoneByEmail(activeTeamConv.otherEmail) ?? '').replace(/[^+\d]/g, '')}`}
                                                title={teamPhoneByEmail(activeTeamConv.otherEmail) ?? undefined}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                                {teamPhoneByEmail(activeTeamConv.otherEmail)}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="thread-messages">
                                {teamMsgsLoading ? (
                                    <div style={{ margin: 'auto' }}>
                                        <LoadingComponent />
                                    </div>
                                ) : teamMessages.length === 0 ? (
                                    <div className="msg-empty">
                                        <div className="msg-empty__icon-circle">
                                            <span role="img" aria-hidden>&#128172;</span>
                                        </div>
                                        <span className="msg-empty__text">{t('messages.teamChat.noMessages')}</span>
                                    </div>
                                ) : (
                                    teamMessages.map((msg, idx) => {
                                        const isMine = msg.mine;
                                        return (
                                            <div
                                                key={msg.id ?? idx}
                                                className={`bubble-row ${isMine ? 'bubble-row--business' : 'bubble-row--client'}`}
                                            >
                                                {!isMine && (
                                                    <Avatar
                                                        name={msg.senderName ?? msg.senderEmail}
                                                        size={28}
                                                    />
                                                )}
                                                <div style={{ maxWidth: '72%' }}>
                                                    <div className={`bubble ${isMine ? 'bubble--business' : 'bubble--client'}`}>
                                                        {msg.text && <span>{msg.text}</span>}
                                                        {(msg.imageKeys ?? []).length > 0 && (
                                                            <div className="bubble-images">
                                                                {(msg.imageKeys ?? []).map((key, ki) => (
                                                                    <ChatImage key={ki} imageKey={key} alt={t('messages.imageAlt')} className="bubble-img" />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="bubble-time" title={absoluteTime(msg.createdAt, timeFmt)}>
                                                        {absoluteTime(msg.createdAt, timeFmt)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={teamMsgsEndRef} />
                            </div>

                            {/* Team Composer (text only) */}
                            <div className="composer">
                                {/* Emoji picker */}
                                {showTeamEmojiPicker && (
                                    <div className="composer-emoji-picker" ref={teamEmojiPickerRef}>
                                        <EmojiPicker
                                            onEmojiClick={handleTeamEmojiClick}
                                            theme={Theme.AUTO}
                                            lazyLoadEmojis
                                            searchPlaceholder={t('messages.emojiSearchPlaceholder')}
                                            width="100%"
                                            height={340}
                                        />
                                    </div>
                                )}
                                <div className="composer-row">
                                    {/* Emoji button */}
                                    <button
                                        ref={teamEmojiButtonRef}
                                        type="button"
                                        className={`composer-btn composer-btn--emoji${showTeamEmojiPicker ? ' active' : ''}`}
                                        onClick={() => setShowTeamEmojiPicker((v) => !v)}
                                        disabled={teamSending}
                                        title={t('messages.emoji')}
                                        aria-label={t('messages.emojiPickerAriaLabel')}
                                        aria-expanded={showTeamEmojiPicker}
                                    >
                                        <span role="img" aria-hidden>&#128522;</span>
                                    </button>

                                    <textarea
                                        ref={teamTextareaRef}
                                        className="composer-textarea"
                                        rows={1}
                                        placeholder={t('messages.teamChat.composerPlaceholder')}
                                        value={teamText}
                                        onChange={(e) => setTeamText(e.target.value)}
                                        onKeyDown={handleTeamKeyDown}
                                        disabled={teamSending}
                                        data-cy="team-messages-input"
                                    />

                                    <button
                                        type="button"
                                        className="composer-btn composer-btn--send"
                                        onClick={handleTeamSend}
                                        disabled={teamSending || !teamText.trim()}
                                        title={t('messages.send')}
                                        aria-label={t('messages.sendAriaLabel')}
                                        data-cy="team-messages-send-btn"
                                    >
                                        {/* send arrow SVG */}
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )
                )}
            </div>
        </div>
    );
};

export default MessagesComponent;
